import React, { useEffect, useMemo, useRef, useState } from 'react';
import { publicApi } from '../utils/api';
import { ChevronDown, Phone } from 'lucide-react';

/**
 * Phone number input with a searchable country-code (dial-code) picker.
 * - Props: value (E.164-ish: "+639XXXXXXXXX"), onChange(value, { country }), defaultIso2
 * - Stores the resolved E.164 string in `value`
 * - Country dropdown shows flag + name + dial code, with search
 */
export function PhoneInput({
  value = '',
  onChange,
  defaultIso2 = 'PH',
  required = false,
  disabled = false,
  className = '',
  inputProps = {},
  'data-testid': testId = 'phone-input',
}) {
  const [countries, setCountries] = useState([]);
  const [selected, setSelected] = useState(null);   // {iso2, name, phonecode, emoji}
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [national, setNational] = useState('');
  const wrapRef = useRef(null);

  // Load country list (with phonecodes) once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await publicApi.get('/api/geo/countries');
        if (cancelled) return;
        const list = (data.countries || []).filter((c) => c.phonecode);
        list.sort((a, b) => a.name.localeCompare(b.name));
        setCountries(list);
        // Pick default from value (if E.164) or fallback to defaultIso2
        if (!selected) {
          if (value && value.startsWith('+')) {
            const v = value.slice(1).replace(/\D/g, '');
            // longest-prefix match against phonecode
            const match = list
              .filter((c) => v.startsWith((c.phonecode || '').replace(/\D/g, '')))
              .sort((a, b) => (b.phonecode?.length || 0) - (a.phonecode?.length || 0))[0];
            if (match) {
              setSelected(match);
              setNational(v.slice(match.phonecode.replace(/\D/g, '').length));
              return;
            }
          }
          const fallback = list.find((c) => c.iso2 === defaultIso2) || list[0];
          if (fallback) setSelected(fallback);
        }
      } catch (e) {
        console.error('Failed to load country codes:', e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Whenever selected/national changes, emit a clean E.164 string
  useEffect(() => {
    if (!selected) return;
    const digits = (national || '').replace(/\D/g, '');
    const code = (selected.phonecode || '').replace(/\D/g, '');
    const composed = digits ? `+${code}${digits}` : '';
    onChange?.(composed, { country: selected });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, national]);

  const filteredCountries = useMemo(() => {
    if (!search) return countries;
    const q = search.toLowerCase();
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.iso2.toLowerCase().includes(q) ||
        (`+${c.phonecode}`).includes(q),
    );
  }, [countries, search]);

  const onNationalChange = (e) => {
    // strip leading zero (common locale habit) since we already have country code
    const raw = e.target.value.replace(/[^\d\s-]/g, '');
    setNational(raw);
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testId}>
      <div className="flex">
        <button
          type="button"
          onClick={() => !disabled && setOpen((o) => !o)}
          disabled={disabled}
          className="flex items-center gap-1.5 px-3 h-10 rounded-l-md bg-[#000B18] border border-r-0 border-white/20 text-white text-sm hover:bg-white/5"
          data-testid={`${testId}-code-btn`}
          aria-label="Select country code"
        >
          {selected ? (
            <>
              <span className="text-lg leading-none">{selected.emoji || '🌐'}</span>
              <span className="font-mono text-xs">+{selected.phonecode}</span>
            </>
          ) : (
            <>
              <Phone className="w-4 h-4" />
              <span>—</span>
            </>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-[#5C7599]" />
        </button>
        <input
          type="tel"
          inputMode="tel"
          value={national}
          onChange={onNationalChange}
          required={required}
          disabled={disabled}
          placeholder="9XX XXX XXXX"
          className="flex-1 h-10 rounded-r-md bg-[#000B18] border border-white/20 text-white px-3 placeholder:text-[#5C7599] focus:outline-none focus:border-[#00E5FF]"
          data-testid={`${testId}-national-input`}
          {...inputProps}
        />
      </div>

      {open && (
        <div
          className="absolute z-50 mt-1 left-0 w-72 max-h-72 overflow-hidden rounded-md bg-[#041A3B] border border-white/20 shadow-xl"
          data-testid={`${testId}-dropdown`}
        >
          <div className="p-2 border-b border-white/10">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country or code…"
              className="w-full px-2.5 py-1.5 rounded bg-[#000B18] border border-white/10 text-white text-sm placeholder:text-[#5C7599] focus:outline-none focus:border-[#00E5FF]"
              data-testid={`${testId}-search-input`}
            />
          </div>
          <div className="overflow-y-auto max-h-56">
            {filteredCountries.length === 0 ? (
              <div className="p-3 text-center text-[#5C7599] text-xs">No matches</div>
            ) : (
              filteredCountries.map((c) => (
                <button
                  key={c.iso2}
                  type="button"
                  onClick={() => {
                    setSelected(c);
                    setOpen(false);
                    setSearch('');
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-white/10 flex items-center justify-between gap-2 text-white"
                  data-testid={`${testId}-option-${c.iso2}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-lg leading-none flex-shrink-0">{c.emoji || '🌐'}</span>
                    <span className="truncate">{c.name}</span>
                  </span>
                  <span className="font-mono text-xs text-[#5C7599] flex-shrink-0">+{c.phonecode}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
